import { TranslocoService } from '@jsverse/transloco';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SnackBarService {
  // milliseconds
  private readonly duration: number = 5000;
  private action$: BehaviorSubject<string> = new BehaviorSubject(null);
  currentAction = this.action$.asObservable();

  constructor(
    public snackBar: MatSnackBar,
    private transloco: TranslocoService,
  ) {
    this.transloco.getActiveLang();
  }

  /**
   * Show snack-bar with custom styles.
   * @param message - message of snack-bar.
   * @param className - name of class for snack-bar styling.
   * @param action - name of name of button which closes snack-bar.
   * @param duration - duration for showing snack-bar.
   */
  private openSnackBar(
    message: string,
    className: string,
    action?: string,
    duration?: number,
  ) {
    this.snackBar
      .open(
        this.transloco.translate(message),
        this.transloco.translate(action),
        {
          duration,
          panelClass: ['x-tention-toast', className],
          horizontalPosition: 'right',
        },
      )
      .onAction()
      .subscribe(() => {
        this.action$.next(action);
      });
  }

  /**
   * Show snack-bar with success styles.
   *
   * @param message message for snack-bar.
   * @param action (name of button which closes snack-bar),
   * @param duration of showing snack-bar
   * if it is null then we this.duration(milliseconds) for showing snack-bar.
   */
  success(message: string, action?: string, duration: number = this.duration) {
    this.openSnackBar(
      message,
      'x-tention-toast-success',
      action,
      action ? null : duration,
    );
  }

  /**
   * Show snack-bar with primary styles.
   *
   * @param message message for snack-bar.
   * @param action - name of button which closes snack-bar,
   * @param duration of showing snack-bar
   * if it is null then we this.duration(milliseconds) for showing snack-bar.
   */
  primary(message: string, action?: string, duration: number = this.duration) {
    this.openSnackBar(
      message,
      'x-tention-toast-primary',
      action,
      action ? null : duration,
    );
  }

  /**
   * Show snack-bar with warning styles.
   *
   * @param message - message for snack-bar
   * @param action - name of button which closes snack-bar,
   * @param duration of showing snack-bar
   * if it is null then we this.duration(milliseconds) for showing snack-bar.
   */
  warn(message: string, action?: string, duration: number = this.duration) {
    this.openSnackBar(
      message,
      'x-tention-toast-warn',
      action,
      action ? null : duration,
    );
  }

  /**
   * Show snack-bar with error styles.
   *
   * @param message - message for snack-bar
   * @param action - name of button which closes snack-bar,
   * @param duration of showing snack-bar
   * if it is null then we this.duration(milliseconds) for showing snack-bar.
   */
  error(message: string, action?: string, duration: number = this.duration) {
    console.error('SB Error: ', message, action, duration);
    this.openSnackBar(
      message,
      'x-tention-toast-error',
      action,
      action ? null : duration,
    );
  }

  /**
   * Show snack-bar with default styles.
   *
   * @param message message for snack-bar.
   * @param action - name of button which closes snack-bar,
   * @param duration of showing snack-bar
   * if it is null then we this.duration(milliseconds) for showing snack-bar.
   */
  default(message: string, action?: string, duration: number = this.duration) {
    this.openSnackBar(message, '', action, action ? null : duration);
  }
}
